import CameraIcon from "@mui/icons-material/Camera";

import { useRef, useState } from "react";
import { CircularProgress, CircularProgressProps } from "@mui/material";
import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";

import Box from "../Box";
import Avatar from "../Avatar";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import ResumableUploadService from "../../services/asset/resumableUpload.service";

import { Constants } from "../../constants/constants";
import { Method } from "../../services/base/HttpClient";
import { camera, hoverBox, avatarBox, circularBox, circularText, circularColor, circularProgress } from "./styles";
import { FileAccessMode, FileProcessingStatus } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";

interface ExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
}

interface IProps {
    src?: string;
    name?: string;
    size?: number;
    label?: string;
    disabled?: boolean;
    shadow?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "inset";
    onChange?: (imageUrl: string, imageId: string) => void;
}

function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
    return (
        <Box sx={circularProgress}>
            <CircularProgress variant="determinate" {...props} sx={circularColor} size={65} />
            <Box sx={circularBox}>
                <Typography variant="caption" color="secondary" component="div" sx={circularText}>
                    {`${Math.round(props.value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}

const AvatarPicker: React.FC<IProps> = ({ size, src, name, label, shadow, onChange, disabled }: IProps) => {
    const getSize = () => {
        return size ? [size, size / 2] : [100, 50];
    };

    const [avatarSize, iconSize] = getSize();

    const onFileChange = async (event: any) => {
        try {
            if (event.target.files && event.target.files[0]) {
                await uploadFile(event.target.files[0]);
            }
        } catch (error: any) {
            const e = Helpers.renderExceptionError(error);
        }
    };

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();

    const imageInputRef = useRef<any>(null);

    const context = useCommonComponentContext();

    const uploadFile = async (file: File) => {
        let validFile = true;
        setUploading(false);
        setUploadProgress(0);

        if (!validFile) {
            imageInputRef.current.value = null;
            Helpers.isFunction(onChange) && onChange(undefined, undefined);
            return;
        }

        try {
            const request = {
                folderPath: Constants.CloudStorage.AVATAR_FOLDER,
                processingStatus: `${FileProcessingStatus.Processed}`,
                accessMode: `${FileAccessMode.Public}`,
                tenantCode: context.tenantCode,
                serviceCode: context.serviceCode,
            };

            const options: UploadOptions = {
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) && req.getMethod() === Method.PATCH) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt(((bytesUploaded / bytesTotal) * 100).toFixed(2));
                    setUploadProgress(percentage);
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        handleUploadError();
                    }

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;

                    Helpers.isFunction(onChange) && onChange(accessUrl, id);
                    setUploading(false);
                },
                onError: (error) => {
                    handleUploadError();
                    console.log("Uploading failed : ", error);
                },
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);
            setUploading(true);
            startOrResumeUpload(tusUpload.current);
        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
            console.log("error: ", error);
        }
    };

    const startOrResumeUpload = (tusUpload: Upload) => {
        tusUpload
            .findPreviousUploads()
            .then((allPreviousUploads) => {
                for (let index = 0; index < allPreviousUploads.length; index++) {
                    const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                    if (previousUpload.uploadUrl === tusUploadUrl.current) {
                        tusUpload.resumeFromPreviousUpload(allPreviousUploads[index]);
                        break;
                    }
                }
                tusUpload.start();
            })
            .catch((error) => {
                console.log("Error when start/resume upload:", error);
            });
    };

    const handleUploadError = () => {
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
            setUploading(false);
            setUploadProgress(0);
        }
        Helpers.isFunction(onChange) && onChange(undefined, undefined);
    };

    return (
        <Box sx={avatarBox}>
            <input
                type="file"
                accept="image/*"
                id="icon-button-file"
                ref={imageInputRef}
                disabled={disabled}
                onChange={onFileChange}
                style={{ display: "none" }}
            />
            <label
                htmlFor="icon-button-file"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {Helpers.isNullOrEmpty(src) ? (
                    <Avatar
                        text={name}
                        alt="profile-image"
                        sx={{
                            fontSize: 75,
                            width: avatarSize,
                            height: avatarSize,
                            fontWeight: "bold",
                            alignItems: "center",
                        }}
                    />
                ) : (
                    <Avatar
                        src={src}
                        alt="profile-image"
                        shadow={shadow ? shadow : "none"}
                        sx={{ width: avatarSize, height: avatarSize, alignItems: "center" }}
                    />
                )}
                {uploading && <CircularProgressWithLabel value={uploadProgress} />}
                <Box id="hoverBox" sx={(theme: any) => hoverBox(theme, { size: avatarSize })} />
                <CameraIcon sx={(theme: any) => camera(theme, { size: iconSize })} />
            </label>
            {label && (
                <Box mt={1} display="flex" justifyContent="center">
                    <Typography variant="caption" fontWeight="bold">
                        {label}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AvatarPicker;
